import {
  ActiveBar,
  ActiveBarContent,
  BottomBarContainer,
  BottomBarContent,
  CancelButton,
  ControlButton,
  Controls,
  DeviceProblemAction,
  ElapsedTimeChip,
  LanguageSelectorButton,
  LanguageSelectorContainer,
  RecordingStatusSection,
  SearchingNotice,
  StartActionRow,
  StartActionSlot,
  StatusTitle,
} from './RecordingBottomNavbar.styles';
import { RecordingPhase } from 'hooks/useRecording';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import { formatLanguageCode, formatTime } from 'utils/audioUpload';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { usePublishBottomNavbarHeight } from 'components/navbars/BottomNavbar';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { messageForState } from 'widgets/audio';
import { STATE_ICONS } from 'widgets/audio/stateIcons';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronDown,
  CircleX,
  Info,
  Mic,
  Pause,
  Play,
  Square,
  XIcon,
} from 'lucide-react';
import { RecordingIndicator } from './RecordingIndicator';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useMicLevel } from 'hooks/useMicLevel';
import { useNoAudioWarning } from 'hooks/useNoAudioWarning';
import { useReconnectBanner } from 'hooks/useReconnectBanner';
import type { ReconnectEpisode } from 'hooks/useRecordingWebSocket';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from 'components';
import { NoAudioWarningDialog } from './NoAudioWarningDialog';
import { RecordingAlert } from './RecordingAlert';
import { AudioRecordingConsent } from './AudioRecordingConsent';
import { BillingScheinAlert } from 'widgets/billingSchein';
import type { RecordingObjectionStatus } from 'types/recordingObjection';

interface RecordingBottomNavbarProps {
  recordingPhase: RecordingPhase;
  elapsedTime: number;
  mediaStream: MediaStream | null;
  selectedLanguageCode: string | undefined;
  languagesData:
    { languages: Array<{ code: string; name: string }>; defaultLanguageCode?: string } | undefined;
  deviceNotFound?: boolean;
  browserNotSupported: boolean;
  reconnectEpisode?: ReconnectEpisode | null;
  medatixxPatientId?: string;
  recordingObjection?: RecordingObjectionStatus;
  isContinuingWithoutRecording?: boolean;
  languageLocked?: boolean;
  actionsDisabled?: boolean;
  onDismissDeviceNotFound?: () => void;
  onToggleRecording: () => void;
  onStopRecording: () => void;
  onStartRecording: () => void;
  onContinueWithoutRecording: () => void;
  onCancelRecording: () => void;
  onLanguageChange: (languageCode: string) => void;
}

export const RecordingBottomNavbar = ({
  recordingPhase,
  elapsedTime,
  mediaStream,
  selectedLanguageCode,
  languagesData,
  deviceNotFound = false,
  browserNotSupported,
  reconnectEpisode = null,
  medatixxPatientId,
  recordingObjection,
  isContinuingWithoutRecording = false,
  languageLocked = false,
  actionsDisabled = false,
  onDismissDeviceNotFound,
  onToggleRecording,
  onStopRecording,
  onStartRecording,
  onContinueWithoutRecording,
  onCancelRecording,
  onLanguageChange,
}: RecordingBottomNavbarProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const audio = useAudioDevice();
  const barRef = useRef<HTMLDivElement>(null);
  usePublishBottomNavbarHeight(barRef);
  // While the device is still being evaluated the record button waits.
  const deviceSettled = audio.ready && audio.state !== 'searching';
  // Idle: replaces the record button. Recording: a banner, because the stream has just gone silent.
  const deviceProblem =
    deviceSettled && !audio.canRecord && audio.state !== 'passive'
      ? { message: messageForState(audio, t), Icon: STATE_ICONS[audio.state] }
      : null;
  const isActiveRecording =
    recordingPhase === RecordingPhase.RECORDING || recordingPhase === RecordingPhase.PAUSED;
  const isPaused = recordingPhase === RecordingPhase.PAUSED;
  const level = useMicLevel(mediaStream, recordingPhase === RecordingPhase.RECORDING);
  const reconnectBanner = useReconnectBanner(reconnectEpisode);
  const { showWarning, showOverlay, showSuccess, openOverlay, dismissOverlay, dismissSuccess } =
    useNoAudioWarning(level, isActiveRecording, isPaused);
  const [showDeviceNotFoundOverlay, setShowDeviceNotFoundOverlay] = useState(false);
  const showConsentNotice = !medatixxPatientId;
  const activeObjection = recordingObjection?.active ? recordingObjection : undefined;

  const selectedLanguageName = selectedLanguageCode
    ? t(`languages.${selectedLanguageCode}`, {
        defaultValue:
          languagesData?.languages.find((language) => language.code === selectedLanguageCode)
            ?.name ?? selectedLanguageCode,
      })
    : undefined;

  const toggle = isPaused
    ? {
        event: ANALYTICS_EVENTS.PATIENT_RESUME_RECORDING,
        ariaLabel: t('recording.resume'),
        label: t('recording.resumeAction'),
        Icon: Play,
      }
    : {
        event: ANALYTICS_EVENTS.PATIENT_PAUSE_RECORDING,
        ariaLabel: t('recording.pause'),
        label: t('recording.pauseAction'),
        Icon: Pause,
      };

  const alertIcon = <AlertCircle className="size-5 shrink-0 text-white mt-0.5" aria-hidden />;

  const LanguageSelector = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <LanguageSelectorContainer>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <LanguageSelectorButton disabled={disabled} aria-label={t('recording.language')}>
              <span className="flex-1 text-left">{formatLanguageCode(selectedLanguageCode)}</span>
              <ChevronDown className="size-4 shrink-0" aria-hidden />
            </LanguageSelectorButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(languagesData?.languages || []).map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.RECORDING_LANGUAGE_CHANGED, {
                    previousLanguageCode: selectedLanguageCode,
                    newLanguageCode: lang.code,
                  });
                  onLanguageChange(lang.code);
                }}
              >
                {formatLanguageCode(lang.code)} -{' '}
                {t(`languages.${lang.code}`, { defaultValue: lang.name })}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </LanguageSelectorContainer>
    );
  };

  return (
    <>
      <NoAudioWarningDialog
        isOpen={showOverlay || showDeviceNotFoundOverlay}
        onDismiss={() => {
          dismissOverlay();
          setShowDeviceNotFoundOverlay(false);
        }}
      />
      <div ref={barRef} className="fixed bottom-0 left-0 right-0 z-50" data-recording-bottom-bar>
        {browserNotSupported && (
          <RecordingAlert
            icon={alertIcon}
            title={t('recording.browserNotSupported.bannerTitle')}
            text={t('recording.browserNotSupported.bannerText')}
          />
        )}
        {reconnectBanner && (
          <RecordingAlert
            icon={alertIcon}
            quietText
            title={t('recording.connectionLost.bannerTitle')}
            text={t('recording.connectionLost.bannerText', {
              seconds: reconnectBanner.seconds,
              reason: t([
                `recording.connectionLost.reason.${reconnectBanner.reasonCode}`,
                'recording.connectionLost.reason.unknown',
              ]),
            })}
          />
        )}
        {deviceNotFound && (
          <RecordingAlert
            icon={alertIcon}
            title={t('recording.deviceNotFound.bannerTitle')}
            text={t('recording.deviceNotFound.bannerText')}
            actions={
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeviceNotFoundOverlay(true)}
                  className="flex items-center gap-1.5 rounded-full border border-white px-3 py-1.5 text-sm font-medium text-white hover:bg-white/10"
                >
                  <Info className="size-4" aria-hidden />
                  {t('recording.deviceNotFound.bannerAction')}
                </button>
                {onDismissDeviceNotFound && (
                  <button
                    type="button"
                    onClick={onDismissDeviceNotFound}
                    className="flex size-8 items-center justify-center rounded-full border border-white text-white hover:bg-white/10"
                    aria-label={t('general.dismiss')}
                  >
                    <XIcon className="size-4" aria-hidden />
                  </button>
                )}
              </div>
            }
          />
        )}
        {isActiveRecording && deviceProblem?.message && (
          <RecordingAlert
            icon={<deviceProblem.Icon className="size-4 shrink-0 mt-0.5" aria-hidden />}
            variant={deviceProblem.message.tone === 'error' ? 'error-soft' : 'warning'}
            title={deviceProblem.message.title}
            text={deviceProblem.message.description ?? ''}
            maskText
          />
        )}
        {showWarning && (
          <RecordingAlert
            icon={alertIcon}
            title={t('recording.noAudioWarning.bannerTitle')}
            text={t('recording.noAudioWarning.bannerText')}
            actions={
              <button
                type="button"
                onClick={openOverlay}
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-white px-3 py-1.5 text-sm font-medium text-white hover:bg-white/10"
              >
                <Info className="size-4" aria-hidden />
                {t('recording.noAudioWarning.bannerAction')}
              </button>
            }
          />
        )}
        {showSuccess && (
          <RecordingAlert
            role="status"
            variant="success"
            icon={<Check className="size-5 shrink-0 text-white mt-0.5" aria-hidden />}
            title={t('recording.noAudioWarning.successTitle')}
            text={t('recording.noAudioWarning.successText')}
            actions={
              <button
                type="button"
                onClick={dismissSuccess}
                className="flex shrink-0 size-8 items-center justify-center rounded-full border border-white text-white hover:bg-white/10"
                aria-label={t('general.dismiss')}
              >
                <XIcon className="size-4" aria-hidden />
              </button>
            }
          />
        )}
        <BillingScheinAlert medatixxPatientId={medatixxPatientId} />
        {recordingPhase === RecordingPhase.IDLE && (
          <div className="bg-white border-t border-border shadow-lg px-4 py-3">
            <BottomBarContainer>
              {showConsentNotice && (
                <AudioRecordingConsent
                  medatixxPatientId={medatixxPatientId}
                  isContinuingWithoutRecording={isContinuingWithoutRecording}
                />
              )}
              <BottomBarContent>
                {!activeObjection && deviceProblem?.message ? (
                  <RecordingAlert
                    icon={<deviceProblem.Icon className="size-4 shrink-0 mt-0.5" aria-hidden />}
                    variant={deviceProblem.message.tone === 'error' ? 'error-soft' : 'warning'}
                    title={deviceProblem.message.title}
                    text={deviceProblem.message.description ?? ''}
                    maskText
                    actions={
                      <DeviceProblemAction
                        $tone={deviceProblem.message.tone}
                        onClick={() => void navigate('/audio-settings')}
                      >
                        {t('audio.actions.openSettings')}
                      </DeviceProblemAction>
                    }
                  />
                ) : (
                  <StartActionRow>
                    <StartActionSlot>
                      {activeObjection ? (
                        <Button
                          fullWidth
                          data-ph-no-autocapture
                          disabled={isContinuingWithoutRecording}
                          onClick={onContinueWithoutRecording}
                        >
                          {t('recording.continueToSummary')}
                          <ArrowRight className="size-4 shrink-0" aria-hidden />
                        </Button>
                      ) : (
                        <>
                          {!deviceSettled && (
                            <SearchingNotice id="recording-device-searching" role="status">
                              {t('audio.messages.searching')}
                            </SearchingNotice>
                          )}
                          <Button
                            fullWidth
                            data-ph-no-autocapture
                            disabled={!deviceSettled}
                            aria-describedby={
                              deviceSettled ? undefined : 'recording-device-searching'
                            }
                            onClick={() => {
                              trackEvent(ANALYTICS_EVENTS.PATIENT_START_RECORDING);
                              onStartRecording();
                            }}
                          >
                            <Mic className="size-4 shrink-0" aria-hidden />
                            {t('recording.start', { defaultValue: 'Gespräch aufzeichnen' })}
                          </Button>
                        </>
                      )}
                    </StartActionSlot>
                    {!languageLocked && <LanguageSelector disabled={Boolean(activeObjection)} />}
                  </StartActionRow>
                )}
              </BottomBarContent>
            </BottomBarContainer>
          </div>
        )}
        {isActiveRecording && (
          <ActiveBar>
            <ActiveBarContent>
              <RecordingStatusSection>
                {isPaused ? <div className="w-6" /> : <RecordingIndicator level={level} />}
                <StatusTitle role="status">
                  {isPaused ? t('recording.stopped') : `${t('recording.running')} ...`}
                  {selectedLanguageName && (
                    <>
                      {' '}
                      <br />({selectedLanguageName})
                    </>
                  )}
                </StatusTitle>
              </RecordingStatusSection>
              <Controls>
                <ControlButton
                  variant="secondary"
                  data-ph-no-autocapture
                  disabled={actionsDisabled}
                  onClick={() => {
                    trackEvent(toggle.event);
                    onToggleRecording();
                  }}
                  aria-label={toggle.ariaLabel}
                >
                  <toggle.Icon aria-hidden />
                  {toggle.label}
                </ControlButton>
                <ControlButton
                  data-ph-no-autocapture
                  disabled={actionsDisabled}
                  onClick={() => {
                    trackEvent(ANALYTICS_EVENTS.PATIENT_STOP_RECORDING);
                    onStopRecording();
                  }}
                  aria-label={t('recording.stop')}
                >
                  <Square aria-hidden />
                  {t('recording.stopAction')}
                  <ElapsedTimeChip>{formatTime(elapsedTime)}</ElapsedTimeChip>
                </ControlButton>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CancelButton
                      data-ph-no-autocapture
                      disabled={actionsDisabled}
                      onClick={onCancelRecording}
                      aria-label={t('recording.cancel')}
                    >
                      <CircleX aria-hidden />
                    </CancelButton>
                  </TooltipTrigger>
                  <TooltipContent>{t('recording.cancel')}</TooltipContent>
                </Tooltip>
              </Controls>
            </ActiveBarContent>
          </ActiveBar>
        )}
      </div>
    </>
  );
};
